/**
 * @module common/DualAxis/dualAxisChartTypeConvert
 * @description 切图 L2 — 组合图与折线双轴 primaryYAxis / secondYAxis 结构互转
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import {
  DEFAULT_TABLE_CODE,
  normalizeLineDualAxisFromStorage,
  normalizeMixedAxisFromStorage,
  resolveMetricFieldValue,
} from './axisMetricUtils';
import type { TAxisMetric } from './types';

export interface IDualAxisConvertResult {
  primaryYAxis: Record<string, unknown>;
  secondYAxis: Record<string, unknown>;
  /** 仅 mixed → 折线且两轴均有指标时写入 true */
  dualYAxisEnabled?: boolean;
}

const MIXED_PRIMARY_CHART_TYPE = 'chart-bar';
const MIXED_SECONDARY_CHART_TYPE = 'chart-line';

function resolveTableCode(
  item: Record<string, unknown>,
): string {
  return String(item.table_code || DEFAULT_TABLE_CODE);
}

function buildMetricPayloadFromQB(columnMetrics: unknown[]): TAxisMetric[] {
  return (columnMetrics as Record<string, unknown>[])
    .map((item) => ({
      metric: resolveMetricFieldValue(item),
      tableCode: resolveTableCode(item),
    }))
    .filter((item) => !!item.metric);
}

function findTableCodeForMetric(
  metric: string,
  columnMetrics: Record<string, unknown>[],
): string {
  const found = columnMetrics.find(
    (item) => resolveMetricFieldValue(item) === metric,
  );
  return found ? resolveTableCode(found) : DEFAULT_TABLE_CODE;
}

function resolveAxisName(
  lineName: string,
  mixedName: string | undefined,
): string {
  return lineName || mixedName || '';
}

function resolveRangeSwitch(
  lineVal: boolean,
  mixedVal: boolean | undefined,
): boolean {
  return lineVal || !!mixedVal;
}

/** Context 层是否同时存在主/次轴对象（切图转换前置条件） */
export function layerHasDualAxisObjects(
  layer: Record<string, unknown>,
): boolean {
  const { primaryYAxis, secondYAxis } = layer;
  return (
    primaryYAxis != null
    && typeof primaryYAxis === 'object'
    && !Array.isArray(primaryYAxis)
    && secondYAxis != null
    && typeof secondYAxis === 'object'
    && !Array.isArray(secondYAxis)
  );
}

export function isDualAxisChartTypeConvertTarget(
  chartType: TChartType,
): boolean {
  return (
    chartType === ChartType.Line
    || chartType === ChartType.EventLine
    || chartType === ChartType.MixedChart
  );
}

/**
 * 转为组合图轴结构：单 metric + 主轴 bar / 次轴 line
 * 主/次指标固定取 QB 统计指标第 1、第 2 个（不从轴内 metrics/metric 继承）
 */
export function convertDualAxisForMixedChart(
  primaryRaw: unknown,
  secondRaw: unknown,
  columnMetrics: unknown[],
): IDualAxisConvertResult {
  const metrics = columnMetrics as Record<string, unknown>[];
  const primaryMetric = metrics[0]
    ? resolveMetricFieldValue(metrics[0])
    : '';
  const secondaryMetric = metrics[1]
    ? resolveMetricFieldValue(metrics[1])
    : '';

  const primaryLine = normalizeLineDualAxisFromStorage(primaryRaw, '');
  const secondaryLine = normalizeLineDualAxisFromStorage(secondRaw, '');
  const primaryMixed = normalizeMixedAxisFromStorage(primaryRaw, {});
  const secondaryMixed = normalizeMixedAxisFromStorage(secondRaw, {});

  return {
    primaryYAxis: {
      yAxisName: resolveAxisName(
        primaryLine.yAxisName || '',
        primaryMixed.yAxisName,
      ),
      metric: primaryMetric,
      chartType: MIXED_PRIMARY_CHART_TYPE,
      yAxisRangeSwitch: resolveRangeSwitch(
        primaryLine.yAxisRangeSwitch,
        primaryMixed.yAxisRangeSwitch,
      ),
    },
    secondYAxis: {
      yAxisName: resolveAxisName(
        secondaryLine.yAxisName || '',
        secondaryMixed.yAxisName,
      ),
      metric: secondaryMetric,
      chartType: MIXED_SECONDARY_CHART_TYPE,
      yAxisRangeSwitch: resolveRangeSwitch(
        secondaryLine.yAxisRangeSwitch,
        secondaryMixed.yAxisRangeSwitch,
      ),
    },
  };
}

/**
 * 转为折线双轴结构：metrics[] + chartType 固定为当前折线图类型
 */
export function convertDualAxisForLineChart(
  primaryRaw: unknown,
  secondRaw: unknown,
  columnMetrics: unknown[],
  chartType: TChartType,
): IDualAxisConvertResult {
  const metrics = columnMetrics as Record<string, unknown>[];
  const qbMetrics = buildMetricPayloadFromQB(metrics);
  const chartTypeStr = chartType as string;

  const primaryLine = normalizeLineDualAxisFromStorage(
    primaryRaw,
    chartTypeStr,
  );
  const secondaryLine = normalizeLineDualAxisFromStorage(
    secondRaw,
    chartTypeStr,
  );
  const primaryMixed = normalizeMixedAxisFromStorage(primaryRaw, {});
  const secondaryMixed = normalizeMixedAxisFromStorage(secondRaw, {});

  let primaryMetrics: TAxisMetric[] = primaryLine.metrics.length > 0
    ? primaryLine.metrics
    : primaryMixed.metric
      ? [{
        metric: primaryMixed.metric,
        tableCode: findTableCodeForMetric(primaryMixed.metric, metrics),
      }]
      : [];

  let secondaryMetrics: TAxisMetric[] = secondaryLine.metrics.length > 0
    ? secondaryLine.metrics
    : secondaryMixed.metric
      ? [{
        metric: secondaryMixed.metric,
        tableCode: findTableCodeForMetric(secondaryMixed.metric, metrics),
      }]
      : [];

  const assigned = new Set([
    ...primaryMetrics.map((item) => item.metric),
    ...secondaryMetrics.map((item) => item.metric),
  ]);
  const unassigned = qbMetrics.filter((item) => !assigned.has(item.metric));
  if (unassigned.length > 0) {
    secondaryMetrics = [...secondaryMetrics, ...unassigned];
  }

  const result: IDualAxisConvertResult = {
    primaryYAxis: {
      yAxisName: resolveAxisName(
        primaryLine.yAxisName || '',
        primaryMixed.yAxisName,
      ),
      metrics: primaryMetrics,
      chartType: chartTypeStr,
      yAxisRangeSwitch: resolveRangeSwitch(
        primaryLine.yAxisRangeSwitch,
        primaryMixed.yAxisRangeSwitch,
      ),
    },
    secondYAxis: {
      yAxisName: resolveAxisName(
        secondaryLine.yAxisName || '',
        secondaryMixed.yAxisName,
      ),
      metrics: secondaryMetrics,
      chartType: chartTypeStr,
      yAxisRangeSwitch: resolveRangeSwitch(
        secondaryLine.yAxisRangeSwitch,
        secondaryMixed.yAxisRangeSwitch,
      ),
    },
  };

  if (primaryMetrics.length > 0 && secondaryMetrics.length > 0) {
    result.dualYAxisEnabled = true;
  }

  return result;
}

export function convertDualAxisForChartType(
  chartType: TChartType,
  primaryRaw: unknown,
  secondRaw: unknown,
  columnMetrics: unknown[],
): IDualAxisConvertResult {
  if (chartType === ChartType.MixedChart) {
    return convertDualAxisForMixedChart(
      primaryRaw,
      secondRaw,
      columnMetrics,
    );
  }
  return convertDualAxisForLineChart(
    primaryRaw,
    secondRaw,
    columnMetrics,
    chartType,
  );
}

